import axios from "axios";
import call from "./call";
import { makeAutoObservable, action, observable, autorun } from "mobx";
import { createContext } from "react";

export const ScreenEnum = Object.freeze({
  NONE: Symbol("none"),
  LOGIN: Symbol("login"),
  DASHBOARD: Symbol("dashboard"),
});

class ScreenStore {
  screen = ScreenEnum.NONE;

  constructor() {
    makeAutoObservable(this, {
      screen: observable,
      setScreen: action,
    });
  }

  setScreen(screen) {
    this.screen = screen;
  }

  get isLogin() {
    return this.screen == ScreenEnum.LOGIN;
  }

  get isDashboard() {
    return this.screen == ScreenEnum.DASHBOARD;
  }
}

export const screenStore = new ScreenStore();
export const ScreenStoreContext = createContext(screenStore);

class UserDataStore {
  _isAuthenticated = false;
  _isResolved = false;
  _pending = null;
  user = null;
  roles = [];

  constructor() {
    makeAutoObservable(this, {
      _isAuthenticated: observable,
      _isResolved: observable,
      _pending: false, // in-flight request, not part of the observable state
      roles: observable,
      user: observable,
      setAuthenticated: action,
      setRoles: action,
      setUser: action,
      fetchMe: false,
      login: false,
      logout: false,
    });
    autorun(() => {
      screenStore.setScreen(
        this.isAuthenticated ? ScreenEnum.DASHBOARD : ScreenEnum.LOGIN,
      );
    });
  }

  setAuthenticated(value) {
    this._isAuthenticated = value === true;
    this._isResolved = true;
    if (!this._isAuthenticated) {
      this.roles = [];
      this.user = null;
    }
  }

  get isAuthenticated() {
    return this._isAuthenticated;
  }

  // False until /api/auth/me has answered once. The session cookie on the backend is
  // the only source of truth, so nothing is cached across reloads.
  get isResolved() {
    return this._isResolved;
  }

  setRoles(value) {
    this.roles = value ?? [];
  }

  setUser(value) {
    this.user = value ?? null;
  }

  // Ask the backend whether there is an active SSO session. Concurrent callers share
  // a single request.
  fetchMe() {
    if (this._pending) {
      return this._pending;
    }

    this._pending = axios({ method: "get", url: "/api/auth/me" })
      .then((response) => {
        this.setUser({ id: response.data.id, email: response.data.email });
        this.setRoles(response.data.userRoles);
        this.setAuthenticated(true);
        return this.user;
      })
      .catch(() => {
        this.setAuthenticated(false);
        return null;
      })
      .finally(() => {
        this._pending = null;
      });

    return this._pending;
  }

  // Hand the browser to the backend, which redirects on to the identity provider.
  login() {
    globalThis.location.href = "/api/auth/login";
  }

  // Clear the backend session, then leave for the sign-in screen. Local state is
  // deliberately left alone: the full page load rebuilds the store, whereas flipping
  // the flag here would wake PrivateRoute's guard and race it back into the identity
  // provider — which, since the provider session survives logout, would sign the user
  // straight back in.
  logout() {
    return axios({ method: "post", url: "/api/auth/logout" })
      .catch(() => {})
      .then(() => {
        globalThis.location.href = "/login";
      });
  }
}

export const userDataStore = new UserDataStore();
export const UserDataStoreContext = createContext(userDataStore);

export const ModalEnum = Object.freeze({
  NONE: Symbol("none"),
  CREATE_TASK: Symbol("createTask"),
  ADD_CLIENT: Symbol("addClient"),
  EDIT_CLIENT: Symbol("editClient"),
  ADD_USER: Symbol("addUser"),
  EDIT_USER: Symbol("editUser"),
  DOWNLOAD_RESULTS: Symbol("downloadResults"),
  SEE_TASK_EVENTS: Symbol("seeTaskEvents"),
});

class ModalStore {
  modal = ModalEnum.NONE;

  constructor() {
    makeAutoObservable(this, {
      modal: observable,
      closeModal: action,
      setModal: action,
    });
  }

  closeModal() {
    this.modal = ModalEnum.NONE;
  }

  setModal(modal) {
    this.modal = modal;
  }

  get isCreateTask() {
    return this.modal == ModalEnum.CREATE_TASK;
  }

  get isAddClient() {
    return this.modal == ModalEnum.ADD_CLIENT;
  }

  get isEditClient() {
    return this.modal == ModalEnum.EDIT_CLIENT;
  }

  get isAddUser() {
    return this.modal == ModalEnum.ADD_USER;
  }

  get isEditUser() {
    return this.modal == ModalEnum.EDIT_USER;
  }

  get isDownloadResults() {
    return this.modal == ModalEnum.DOWNLOAD_RESULTS;
  }

  get isSeeTaskEvents() {
    return this.modal == ModalEnum.SEE_TASK_EVENTS;
  }
}

export const modalStore = new ModalStore();
export const ModalStoreContext = createContext(modalStore);

class DownloadResultsModalStore {
  isLoading = false;
  error = "";
  buyingStages = [];
  selectedBuyingStages = [];
  taskId = 0;

  constructor() {
    makeAutoObservable(this, {
      isLoading: observable,
      error: observable,
      buyingStages: observable,
      selectedBuyingStages: observable,
      taskId: observable,
      setBuyingStages: action,
      setSelectedBuyingStages: action,
      setTaskId: action,
    });
    autorun(() => {
      const taskId = this.taskId;
      if (taskId == 0) {
        return;
      }
      this.setLoading(true);
      call(
        axios({
          method: "get",
          url: "/buying-stage",
          params: { task_id: taskId },
        }),
      )
        .then((response) => {
          this.setBuyingStages(response.data);
          this.setLoading(false);
        })
        .catch((error) => {
          this.setLoading(false);
          this.setError(error.response.data.error);
        });
    });
  }

  setLoading(value) {
    this.isLoading = value;
  }

  setError(value) {
    this.error = value;
  }

  setBuyingStages(stages) {
    this.buyingStages = stages;
  }

  setSelectedBuyingStages(stages) {
    this.selectedBuyingStages = stages;
  }

  setTaskId(taskId) {
    this.taskId = taskId;
  }
}
export const downloadResultsModalStore = new DownloadResultsModalStore();
export const DownloadResultsModalStoreContext = createContext(
  downloadResultsModalStore,
);

class AddUserModalStore {
  users;
  selectedUser;
  roles;
  selectedRoles;
  isLoading;
  isDeleteButtonEnabled;
  isInEditMode;
  isUsernameEmpty;
  isPasswordsNotMatch;
  isPasswordNotCorrect;
  password;
  passwordConfirm;
  error;

  constructor() {
    this.init();
    makeAutoObservable(this, {
      users: observable,
      selectedUser: observable,
      roles: observable,
      selectedRoles: observable,
      isLoading: observable,
      isDeleteButtonEnabled: observable,
      isInEditMode: observable,
      isUsernameEmpty: observable,
      isPasswordsNotMatch: observable,
      isPasswordNotCorrect: observable,
      password: observable,
      passwordConfirm: observable,
      error: observable,
      setUsers: action,
      setSelectedUser: action,
      setSelectedRoles: action,
      setDeleteButtonEnabled: action,
      setLoading: action,
      setInEditMode: action,
      setUsernameEmpty: action,
      setPasswordsNotMatch: action,
      setPasswordNotCorrect: action,
      setPassword: action,
      setPasswordConfirm: action,
      setError: action,
      reset: action,
    });
    autorun(() => {
      if (!modalStore.isAddUser && !modalStore.isEditUser) {
        this.reset();
        return;
      }

      if (this.isInEditMode) {
        this.setError("");
        this.setLoading(true);
        call(
          axios({
            method: "get",
            url: "/api/g2/user",
          }),
        )
          .then((response) => {
            this.setUsers([response.data]);
            this.setLoading(false);
          })
          .catch((error) => {
            this.setLoading(false);
            this.setError(error.response.data.error);
          });
      }
    });
    autorun(() => {
      this.setPasswordsNotMatch(this.password != this.passwordConfirm);
      this.setPasswordNotCorrect(
        this.password.length > 0 && this.password.length < 8,
      );
    });
  }

  init() {
    this.users = [];
    this.selectedUser = null;
    this.roles = ['ROLE_ADMIN', 'ROLE_USER'];
    this.selectedRoles = [];
    this.isLoading = false;
    this.isDeleteButtonEnabled = false;
    this.isInEditMode = false;
    this.isPasswordsNotMatch = false;
    this.isPasswordNotCorrect = false;
    this.password = "";
    this.passwordConfirm = "";
    this.error = "";
  }

  setUsers(users) {
    this.users = users;
  }

  setSelectedUser(user) {
    this.selectedUser = user;
  }

  setSelectedRoles(roles) {
    this.selectedRoles = roles;
  }

  setDeleteButtonEnabled(value) {
    this.isDeleteButtonEnabled = value;
  }

  setLoading(value) {
    this.isLoading = value;
  }

  setInEditMode(value) {
    this.isInEditMode = value;
  }

  setUsernameEmpty(value) {
    this.isUsernameEmpty = value;
  }

  setPasswordsNotMatch(value) {
    this.isPasswordsNotMatch = value;
  }

  setPasswordNotCorrect(value) {
    this.isPasswordNotCorrect = value;
  }

  setPassword(value) {
    this.password = value;
  }

  setPasswordConfirm(value) {
    this.passwordConfirm = value;
  }

  setError(value) {
    this.error = value;
  }

  reset() {
    this.init();
  }

  get isFormValid() {
    return (
      !this.isUsernameEmpty &&
      !this.isPasswordsNotMatch &&
      !this.isPasswordNotCorrect
    );
  }
}
export const addUserModalStore = new AddUserModalStore();
export const AddUserModalStoreContext = createContext(addUserModalStore);

class SeeTaskEventsModalStore {
  taskId;
  isLoading;
  error;
  events;

  constructor() {
    this.init();
    makeAutoObservable(this, {
      taskId: observable,
      isLoading: observable,
      error: observable,
      events: observable,
      setEvents: action,
      reset: action,
    });
    autorun(() => {
      if (!this.taskId) {
        this.reset();
        return;
      }

      this.setLoading(true);
      call(
        axios({
          method: "get",
          url: `/api/g2/task/${this.taskId}/events`,
          headers: {
            Accept: "application/json",
          },
        }),
      )
        .then((response) => {
          this.setEvents(response.data);
          this.setLoading(false);
        })
        .catch((error) => {
          this.setError(error.response.data.error);
          this.setLoading(false);
        });
    });
  }

  init() {
    this.taskId = 0;
    this.isLoading = false;
    this.error = "";
    this.events = [];
  }

  setTaskId(taskId) {
    this.reset();
    this.taskId = taskId;
  }

  setLoading(value) {
    this.isLoading = value;
  }

  setError(value) {
    this.error = value;
  }

  setEvents(events) {
    this.events = events;
  }

  reset() {
    this.init();
  }
}
export const seeTaskEventsModalStore = new SeeTaskEventsModalStore();
export const SeeTaskEventsModalStoreContext = createContext(
  seeTaskEventsModalStore,
);

class CreateTaskModalStore {
  isLoading;
  error;
  clients;
  selectedClient;
  cids;

  constructor() {
    this.init();
    makeAutoObservable(this, {
      isLoading: observable,
      error: observable,
      clients: observable,
      selectedClient: observable,
      cids: observable,
      dateFrom: observable,
      dateTo: observable,
      reset: action,
      setLoading: action,
      setError: action,
      setClients: action,
      setSelectedClient: action,
      setCids: action,
      setDateFrom: action,
      setDateTo: action,
    });

    autorun(() => {
      if (!modalStore.isCreateTask) {
        this.reset();
        return;
      }

      this.isLoading = true;
      call(
        axios({
          method: "get",
          url: "/api/g2/client",
        }),
      )
        .then((response) => {
          this.setClients(response.data);
          this.setLoading(false);
        })
        .catch((error) => {
          this.setError(error.response.data.error);
          this.setLoading(false);
        });
    });
  }

  init() {
    this.isLoading = false;
    this.error = "";
    this.clients = [];
    this.selectedClient = null;
    this.cids = [];
    this.dateFrom = "";
    this.dateTo = "";
  }

  reset() {
    this.init();
  }

  setLoading(value) {
    this.isLoading = value;
  }

  setError(value) {
    this.error = value;
  }

  setClients(value) {
    this.clients = value;
  }

  setSelectedClient(value) {
    this.selectedClient = value;
  }

  setCids(value) {
    this.cids = value;
  }

  setDateFrom(value) {
    this.dateFrom = value;
  }

  setDateTo(value) {
    this.dateTo = value;
  }
}
export const createTaskModalStore = new CreateTaskModalStore();
export const CreateTaskModalStoreContext = createContext(createTaskModalStore);
